import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { LoadingSections, OverlayComponent, SharedStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const initialState: SharedStateType = {
  loading: false,
  notification: null,
  modal: undefined,
  overlay: undefined,
  navigateURL: "",

  roles: [],
  permissions: [],
  user: null,

  loadingSections: {},
  loadingTypes: [],
};

const reducer = createReducer<SharedStateType, Action>(initialState)
  .handleAction(actions.startLoading, (state) =>
    produce(state, (nextState) => {
      nextState.loading = true;
    }),
  )
  .handleAction(actions.stopLoading, (state) =>
    produce(state, (nextState) => {
      nextState.loading = false;
    }),
  )
  .handleAction(actions.showNotification, (state, action) =>
    produce(state, (nexState) => {
      nexState.notification = action.payload;
    }),
  )
  .handleAction(actions.showModal, (state, action) =>
    produce(state, (nexState) => {
      nexState.modal = action.payload;
    }),
  )
  .handleAction(actions.hideModal, (state) =>
    produce(state, (nexState) => {
      nexState.modal = undefined;
    }),
  )
  .handleAction(actions.updateOverlay, (state, action) =>
    produce(state, (nexState) => {
      if (state.overlay) {
        nexState.overlay = { ...nexState.overlay, ...action.payload } as OverlayComponent;
      }
    }),
  )
  .handleAction(actions.showOverlay, (state, action) =>
    produce(state, (nexState) => {
      nexState.overlay = action.payload;
    }),
  )
  .handleAction(actions.hideOverlay, (state) =>
    produce(state, (nexState) => {
      nexState.overlay = undefined;
    }),
  )
  .handleAction(actions.setSidebar, (state, action) =>
    produce(state, (nexState) => {
      nexState.sidebar = action.payload;
    }),
  )
  .handleAction(actions.clearSidebar, (state) =>
    produce(state, (nexState) => {
      nexState.sidebar = undefined;
    }),
  )
  .handleAction(actions.getRoles.success, (state, action) =>
    produce(state, (nexState) => {
      nexState.roles = action.payload;
    }),
  )
  .handleAction(actions.getPermissions.success, (state, action) =>
    produce(state, (nexState) => {
      nexState.permissions = action.payload;
    }),
  )
  .handleAction(actions.addLoadingType, (state, action) =>
    produce(state, (nextState) => {
      nextState.loadingTypes = [...state.loadingTypes, action.payload];
    }),
  )
  .handleAction(actions.removeLoadingType, (state, action) =>
    produce(state, (nextState) => {
      nextState.loadingTypes = state.loadingTypes.filter(({ name }) => name !== action.payload);
      nextState.loadingSections = {};
    }),
  )
  .handleAction(actions.addLoadingSection, (state, action) =>
    produce(state, (nextState) => {
      const loadingSections: LoadingSections = { ...state.loadingSections };

      if (!loadingSections[action.payload.loadingSection]) {
        loadingSections[action.payload.loadingSection] = [];
      }

      loadingSections[action.payload.loadingSection] = [
        ...Array.from(new Set([...loadingSections[action.payload.loadingSection], action.payload.requestName])),
      ];

      nextState.loadingSections = loadingSections;
    }),
  )
  .handleAction(actions.removeLoadingSection, (state, action) =>
    produce(state, (nextState) => {
      const loadingSections = { ...state.loadingSections };

      if (loadingSections[action.payload.loadingSection]) {
        loadingSections[action.payload.loadingSection] = loadingSections[action.payload.loadingSection].filter(
          (loadingRequest) => loadingRequest !== action.payload.requestName,
        );

        if (!loadingSections[action.payload.loadingSection].length) {
          delete loadingSections[action.payload.loadingSection];
        }

        nextState.loadingSections = loadingSections;
      }
    }),
  )
  .handleAction(actions.getUserDetails.success, (state, action) =>
    produce(state, (nexState) => {
      nexState.user = action.payload;
    }),
  )
  .handleAction(actions.navigate, (state, action) =>
    produce(state, (nexState) => {
      nexState.navigateURL = action.payload;
    }),
  );

export { reducer as SharedReducer };
